import axios, { AxiosInstance, AxiosError } from 'axios';
import type {
  AdminSessionsResponse,
  FileListResponse,
  LoginRequest,
  LoginResponse,
  ProfileStatus,
  Session,
  ApiError,
  TOTPSetupResponse,
  UploadResponse,
  WebAuthnOptionsResponse,
} from '../types';

class ApiService {
  private client: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: '/api/v1',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use((config) => {
      if (this.token) {
        config.headers.Authorization = `Bearer ${this.token}`;
      }
      return config;
    });

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError<ApiError>) => {
        if (error.response?.status === 401) {
          // Token expired or invalid - clear auth state
          this.clearToken();
          localStorage.removeItem('vdi_user');
          // Don't redirect - let React handle the state change
        }
        return Promise.reject(error);
      }
    );

    // Load token from localStorage
    const storedToken = localStorage.getItem('vdi_token');
    if (storedToken) {
      this.token = storedToken;
    }
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('vdi_token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('vdi_token');
  }

  getToken(): string | null {
    return this.token;
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await this.client.post<LoginResponse>('/login', credentials);
    this.setToken(response.data.token);
    return response.data;
  }

  async prepareTOTP(setupToken: string): Promise<TOTPSetupResponse> {
    const response = await this.client.post<TOTPSetupResponse>('/mfa/totp/prepare', {
      setup_token: setupToken,
    });
    return response.data;
  }

  async finishTOTP(setupToken: string, code: string): Promise<LoginResponse> {
    const response = await this.client.post<LoginResponse>('/mfa/totp/finish', {
      setup_token: setupToken,
      code,
    });
    this.setToken(response.data.token);
    return response.data;
  }

  async verifyTOTP(challengeToken: string, code: string): Promise<LoginResponse> {
    const response = await this.client.post<LoginResponse>('/mfa/totp/verify', {
      challenge_token: challengeToken,
      code,
    });
    this.setToken(response.data.token);
    return response.data;
  }

  async beginWebAuthnRegistration(setupToken: string): Promise<WebAuthnOptionsResponse> {
    const response = await this.client.post<WebAuthnOptionsResponse>('/mfa/webauthn/register/begin', {
      setup_token: setupToken,
    });
    return response.data;
  }

  async finishWebAuthnRegistration(setupToken: string, credential: unknown): Promise<LoginResponse> {
    const response = await this.client.post<LoginResponse>('/mfa/webauthn/register/finish', {
      setup_token: setupToken,
      credential,
    });
    this.setToken(response.data.token);
    return response.data;
  }

  async beginWebAuthnLogin(challengeToken: string): Promise<WebAuthnOptionsResponse> {
    const response = await this.client.post<WebAuthnOptionsResponse>('/mfa/webauthn/login/begin', {
      challenge_token: challengeToken,
    });
    return response.data;
  }

  async finishWebAuthnLogin(challengeToken: string, credential: unknown): Promise<LoginResponse> {
    const response = await this.client.post<LoginResponse>('/mfa/webauthn/login/finish', {
      challenge_token: challengeToken,
      credential,
    });
    this.setToken(response.data.token);
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await this.client.post('/logout');
    } finally {
      this.clearToken();
    }
  }

  // Session endpoints

  // Unified session connection endpoint
  async connectSession(forceNew: boolean = false): Promise<Session> {
    const params = forceNew ? '?force_new=true' : '';
    const response = await this.client.post<Session>(`/connect_session${params}`);
    return response.data;
  }

  // Legacy endpoints (kept for backwards compatibility)
  async createSession(): Promise<Session> {
    const response = await this.client.post<Session>('/sessions');
    return response.data;
  }

  async getSession(id: string): Promise<Session> {
    const response = await this.client.get<Session>(`/sessions/${id}`);
    return response.data;
  }

  async getUserSession(): Promise<Session> {
    const response = await this.client.get<Session>('/session');
    return response.data;
  }

  async pauseSession(id: string): Promise<void> {
    await this.client.post(`/sessions/${id}/pause`);
  }

  async destroySession(id: string): Promise<void> {
    console.log('[API] Destroying session:', id);
    console.log('[API] Auth token present:', !!this.token);
    try {
      const response = await this.client.delete(`/sessions/${id}`);
      console.log('[API] Destroy session response:', response.status);
    } catch (error) {
      console.error('[API] Destroy session error:', error);
      throw error;
    }
  }

  async listSessions(): Promise<Session[]> {
    const response = await this.client.get<Session[]>('/sessions');
    return response.data;
  }

  async listAdminSessions(): Promise<Session[]> {
    const response = await this.client.get<AdminSessionsResponse>('/admin/sessions');
    return response.data.sessions;
  }

  async destroyAdminSession(id: string): Promise<void> {
    await this.client.delete(`/admin/sessions/${encodeURIComponent(id)}`);
  }

  async resetUserMFA(username: string): Promise<void> {
    await this.client.post('/admin/mfa/reset', { username });
  }

  async resetOwnMFA(): Promise<void> {
    await this.client.post('/mfa/reset-self');
  }

  async getProfileStatus(): Promise<ProfileStatus> {
    const response = await this.client.get<ProfileStatus>('/profile');
    return response.data;
  }

  async deleteProfile(): Promise<void> {
    await this.client.delete('/profile');
  }

  // File transfer endpoints
  async listFiles(path: string = '/home/kasm-user/Desktop'): Promise<FileListResponse> {
    const response = await this.client.get<FileListResponse>('/files/list', {
      params: { path },
    });
    return response.data;
  }

  async uploadFile(
    file: File,
    path?: string,
    onProgress?: (percent: number) => void
  ): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (path) formData.append('path', path);

    const response = await this.client.post('/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
      onUploadProgress: (event) => {
        if (onProgress && event.total) {
          onProgress(Math.round((event.loaded * 100) / event.total));
        }
      },
    });
    return response.data;
  }

  async uploadProfileFile(
    file: File,
    options: { isCui: boolean; source?: string; note?: string },
    onProgress?: (percent: number) => void
  ): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('is_cui', options.isCui ? 'true' : 'false');
    if (options.source) formData.append('source', options.source);
    if (options.note) formData.append('note', options.note);

    const response = await this.client.post<UploadResponse>('/files/profile-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
      onUploadProgress: (event) => {
        if (onProgress && event.total) {
          onProgress(Math.round((event.loaded * 100) / event.total));
        }
      },
    });
    return response.data;
  }

  getDownloadUrl(filePath: string): string {
    const token = this.getToken();
    return `/api/v1/files/download?path=${encodeURIComponent(filePath)}&token=${token}`;
  }
}

// Export singleton instance
export const api = new ApiService();
